import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getPostById, deletePost } from '../services/postService'; // Assuming you have this service function
import { useAuth } from '../context/AuthContext'; // Added useAuth
import { Box, Typography, Button, Paper, Avatar, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material'; // Removed Grid, CircularProgress, Added Avatar
import PersonIcon from '@mui/icons-material/Person'; // Added PersonIcon
import './PostView.css';
import 'react-quill-new/dist/quill.snow.css'; // Import Quill styles for rendering

// Helper function to format date and time
const formatFullDate = (timestamp) => {
  if (!timestamp) return 'Date not available';

  let date;
  if (timestamp.seconds) { // Firebase Timestamp object
    date = new Date(timestamp.seconds * 1000 + (timestamp.nanoseconds || 0) / 1000000);
  } else if (typeof timestamp === 'string' || typeof timestamp === 'number') { // ISO string or milliseconds
    date = new Date(timestamp);
  } else {
    return 'Invalid date format';
  }

  if (isNaN(date.getTime())) {
    return 'Invalid date';
  }

  const options = { month: 'long', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' };
  return date.toLocaleString(undefined, options);
};

const PostView = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { firestoreUser } = useAuth(); // Get current user
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        const postData = await getPostById(postId);
        if (postData) {
          setPost(postData);
        } else {
          setError('Post not found.');
        }
      } catch (err) {
        console.error('Error fetching post:', err);
        setError(err.message || 'Failed to fetch post.');
      }
      setLoading(false);
    };

    if (postId) {
      fetchPost();
    }
  }, [postId]);

  if (loading) return <Typography sx={{ textAlign: 'center', mt: 4, color: '#F5F5F5' }}>Loading post...</Typography>;
  if (error) return <Typography color="error" sx={{ textAlign: 'center', mt: 4, color: '#F5F5F5' }}>Error loading post: {error}</Typography>;
  if (!post) return <Typography sx={{ textAlign: 'center', mt: 4, color: '#F5F5F5' }}>Post not found.</Typography>;

  const formattedDate = formatFullDate(post.createdAt); // Use the defined helper
  const authorName = post.postType === 'inCharacter' ? post.characterName : post.userName;
  const isAdmin = firestoreUser && firestoreUser.roles && firestoreUser.roles.admin;
  const isAuthor = post && firestoreUser && post.userId === firestoreUser.uid;

  const handleDelete = async () => {
    try {
      await deletePost(postId);
      navigate('/town-square'); // Navigate back to town square after deletion
    } catch (err) {
      console.error("Error deleting post:", err);
      setError(err.message || "Failed to delete post.");
    }
  };

  const openDeleteConfirm = () => setShowDeleteConfirm(true);
  const closeDeleteConfirm = () => setShowDeleteConfirm(false);

  const confirmDelete = () => {
    handleDelete();
    closeDeleteConfirm();
  };

  return (
    <>
      <Box sx={{ 
        backgroundColor: '#f5f5dc', 
        minHeight: '100vh',
        py: 4,
        px: { xs: 2, sm: 3 }
      }}>
      <Box sx={{ 
        maxWidth: { xs: '100%', sm: '800px', md: '900px' }, 
        margin: '0 auto'
      }}>
        <Paper 
          elevation={6} 
          sx={{
            backgroundColor: '#fffacd',
            border: '2px solid #d2b48c',
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
          }}
        >
          {/* Top Navigation Bar */}
          <Box sx={{ 
            backgroundColor: 'rgba(139, 69, 19, 0.05)',
            p: 2,
            borderBottom: '1px solid #d2b48c',
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center'
          }}>
            {/* Back Button - Top Left */}
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              startIcon={<span>←</span>}
              sx={{ 
                borderColor: '#8b4513',
                color: '#8b4513',
                fontFamily: '"Georgia", serif',
                fontSize: '0.9rem',
                textTransform: 'none',
                '&:hover': { 
                  borderColor: '#704214',
                  backgroundColor: 'rgba(139, 69, 19, 0.1)',
                  color: '#704214'
                },
                transition: 'all 0.2s ease'
              }}
            >
              Back
            </Button>

            {/* Admin/Author Actions - Top Right */}
            {(isAdmin || isAuthor) && (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button 
                  component={Link} 
                  to={`/post/${postId}/edit`}
                  variant="contained" 
                  size="small"
                  startIcon={<span>✏️</span>}
                  sx={{
                    backgroundColor: '#8b4513',
                    color: 'white',
                    fontFamily: '"Georgia", serif',
                    textTransform: 'none',
                    '&:hover': { 
                      backgroundColor: '#704214',
                      transform: 'translateY(-1px)'
                    },
                    transition: 'all 0.2s ease'
                  }} 
                >
                  Edit Post
                </Button>
                {isAdmin && (
                  <Button 
                    variant="contained" 
                    color="error" 
                    onClick={openDeleteConfirm} 
                    size="small"
                    startIcon={<span>🗑️</span>}
                    sx={{
                      fontFamily: '"Georgia", serif',
                      textTransform: 'none',
                      '&:hover': { 
                        transform: 'translateY(-1px)'
                      },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    Delete Post
                  </Button>
                )}
              </Box>
            )}
          </Box>

          {/* Modern Header Section */}
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography 
              variant="h4" 
              component="h1" 
              sx={{ 
                color: '#5c3c10', 
                fontWeight: 'bold', 
                fontFamily: '"Georgia", serif',
                mb: 3,
                fontSize: { xs: '1.8rem', md: '2.25rem' }
              }}
            >
              {post.title}
            </Typography>
            
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              gap: 2
            }}>
              {post.postType === 'inCharacter' && (
                <Box sx={{ position: 'relative' }}>
                  {post.characterImageUrl ? (
                    <Avatar 
                      src={post.characterImageUrl} 
                      alt={post.characterName || 'Character'}
                      sx={{ 
                        width: 80, 
                        height: 80, 
                        border: '3px solid #8b4513',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                      }}
                    />
                  ) : (
                    <Avatar sx={{ 
                      width: 80, 
                      height: 80, 
                      backgroundColor: '#8b4513', 
                      border: '3px solid #704214',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                    }}>
                      <PersonIcon sx={{ fontSize: '2.5rem', color: '#f5f5dc' }} />
                    </Avatar>
                  )}
                </Box>
              )}
              
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2,
                flexWrap: 'wrap',
                justifyContent: 'center'
              }}>
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    color: '#704214', 
                    fontFamily: '"Georgia", serif',
                    fontWeight: 'bold'
                  }}
                >
                  by {authorName || 'Unknown Author'}
                </Typography>
                <Box sx={{ 
                  width: 4, 
                  height: 4, 
                  borderRadius: '50%', 
                  backgroundColor: '#704214' 
                }} />
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    color: '#8b4513', 
                    fontFamily: '"Georgia", serif',
                    fontStyle: 'italic'
                  }}
                >
                  {formattedDate}
                </Typography>
              </Box>
            </Box>
          </Box>
          {/* Modern Content Section */}
          <Box sx={{ px: 4, pb: 2 }}>
            <Box
              sx={{
                backgroundColor: '#f0e6d2',
                padding: 3,
                borderRadius: 2,
                border: '1px solid #d2b48c',
                fontFamily: '"Georgia", serif',
                lineHeight: 1.7,
                
                '& p': { 
                  marginBottom: '1.2em', 
                  color: '#5c3c10',
                  fontSize: '1rem'
                },
                '& h1, & h2, & h3, & h4, & h5, & h6': { 
                  marginTop: '1.8em', 
                  marginBottom: '0.8em', 
                  color: '#8b4513', 
                  fontWeight: 'bold' 
                },
                '& ul, & ol': { 
                  marginLeft: '2em', 
                  marginBottom: '1.2em',
                  '& li': { 
                    marginBottom: '0.6em',
                    color: '#5c3c10'
                  }
                },
                '& blockquote': {
                  borderLeft: '4px solid #8b4513',
                  backgroundColor: 'rgba(139, 69, 19, 0.05)',
                  paddingLeft: '1.5em',
                  paddingTop: '1em',
                  paddingBottom: '1em',
                  marginLeft: '0',
                  marginRight: '0',
                  fontStyle: 'italic',
                  color: '#704214',
                  borderRadius: '0 8px 8px 0'
                },
                '& pre': {
                  backgroundColor: '#2d2d2d',
                  color: '#f8f8f2',
                  padding: '1.2em',
                  overflowX: 'auto',
                  borderRadius: '8px',
                  fontFamily: 'monospace',
                  fontSize: '0.9em',
                  border: '1px solid #555',
                  marginY: '1.5em'
                },
                '& code': {
                  backgroundColor: '#2d2d2d',
                  color: '#f8f8f2',
                  padding: '0.3em 0.5em',
                  borderRadius: '4px',
                  fontFamily: 'monospace',
                  fontSize: '0.9em'
                },
                '& img': { 
                  maxWidth: '100%', 
                  height: 'auto', 
                  borderRadius: '8px', 
                  marginY: '1.5em',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                },
                '& table': {
                  width: '100%',
                  borderCollapse: 'collapse',
                  marginY: '1.5em',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  '& th, & td': {
                    border: '1px solid #d2b48c',
                    padding: '12px',
                    textAlign: 'left'
                  },
                  '& th': {
                    backgroundColor: '#8b4513',
                    color: '#f5f5dc',
                    fontWeight: 'bold'
                  },
                  '& td': {
                    backgroundColor: '#fffacd'
                  }
                },
                '& a': {
                  color: '#8b4513',
                  textDecoration: 'underline',
                  '&:hover': {
                    color: '#704214',
                    textDecoration: 'none'
                  }
                }
              }}
            >
              <div className="ql-snow">
                <div 
                  className="ql-editor"
                  dangerouslySetInnerHTML={{ __html: post.content }}
                />
              </div>
            </Box>
          </Box>
          
          {/* Modern Footer */}
          <Box sx={{ 
            p: 3, 
            borderTop: '1px solid #d2b48c',
            backgroundColor: 'rgba(139, 69, 19, 0.02)',
            display: 'flex',
            justifyContent: 'center'
          }}>
            <Button
              variant="contained"
              onClick={() => navigate(-1)}
              sx={{ 
                backgroundColor: '#8b4513',
                color: '#f5f5dc',
                fontFamily: '"Georgia", serif',
                px: 4,
                py: 1.5,
                fontSize: '0.95rem',
                textTransform: 'none',
                borderRadius: 2,
                '&:hover': { 
                  backgroundColor: '#704214',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              ← Back to Conference Clamp
            </Button>
          </Box>
        </Paper>
      </Box>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDeleteConfirm}
        onClose={closeDeleteConfirm}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          sx: {
            backgroundColor: '#fffacd',
            border: '2px solid #d2b48c'
          }
        }}
      >
        <DialogTitle 
          id="alert-dialog-title"
          sx={{ 
            fontFamily: '"Georgia", serif',
            color: '#5c3c10',
            fontWeight: 'bold'
          }}
        >
          Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <DialogContentText 
            id="alert-dialog-description"
            sx={{ 
              fontFamily: '"Georgia", serif',
              color: '#5c3c10'
            }}
          >
            Are you sure you want to delete this post? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={closeDeleteConfirm}
            sx={{
              color: '#5c3c10',
              fontFamily: '"Georgia", serif',
              textTransform: 'none'
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmDelete}
            variant="contained"
            color="error" 
            autoFocus
            sx={{
              fontFamily: '"Georgia", serif',
              textTransform: 'none'
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </>
  );
};

export default PostView;
